import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, ExternalLink, Shield, Building, Receipt, CreditCard, MapPin, Award, Mail, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { useCallback } from "react";
import type { LegalDocument } from "@/types";
import { SectionHeading } from "@/components/ui/section-heading";

const LegalDocuments = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isArabic = language === 'ar';

  const documents: LegalDocument[] = [
    {
      title: t.commercialRegTitle,
      description: t.commercialRegDesc,
      icon: Building,
      file: "/documents/commercial-registration.pdf",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: t.vatRegTitle,
      description: t.vatRegDesc,
      icon: Receipt,
      file: "/documents/vat-registration.pdf",
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: t.chamberTitle,
      description: t.chamberDesc,
      icon: Shield,
      file: "/documents/chamber-membership.pdf",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      title: t.nationalAddressTitle,
      description: t.nationalAddressDesc,
      icon: MapPin,
      file: "/documents/national-address-jubail.pdf",
      color: "text-indigo-600",
      bgColor: "bg-indigo-100",
    },
  ] as const;

  // Secure document handlers with proper error handling and validation
  const handleViewDocument = useCallback((filePath: string): void => {
    try {
      // Validate file path to prevent path traversal
      if (!filePath.startsWith('/documents/') || !filePath.endsWith('.pdf')) {
        console.error('Invalid document path:', filePath);
        return;
      }
      
      const newWindow = window.open(filePath, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.error('Popup blocked or failed to open document');
      }
    } catch (error) {
      console.error('Error opening document:', error);
    }
  }, []);

  const handleDownloadDocument = useCallback((filePath: string, title: string): void => {
    try {
      // Validate inputs
      if (!filePath.startsWith('/documents/') || !filePath.endsWith('.pdf')) {
        console.error('Invalid document path:', filePath);
        return;
      }
      
      // Sanitize filename
      const sanitizedTitle = title.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      
      const link = document.createElement('a');
      link.href = filePath;
      link.download = `${sanitizedTitle}.pdf`;
      link.setAttribute('rel', 'noopener noreferrer');
      
      // Append to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  }, []);

  return (
    <section className="py-20 bg-muted/30" id="documents">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <SectionHeading
            centered
            eyebrow={isArabic ? "الاعتمادات" : "Credentials"}
            wrapperClassName="mb-6"
          >
            {t.documentsTitle}
          </SectionHeading>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t.documentsSubtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {documents.map((doc, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-elegant transition-all duration-300 border-0 bg-card/80 backdrop-blur-sm hover:scale-105 card-animate"
            >
              <CardHeader className="text-center pb-4">
                <div className={`w-16 h-16 ${doc.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 icon-animate`}>
                  <doc.icon className={`w-8 h-8 ${doc.color}`} />
                </div>
                <CardTitle className="text-xl font-bold text-accent">{doc.title}</CardTitle>
                <CardDescription className="text-center">
                  {doc.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-border/50"
                    onClick={() => handleViewDocument(doc.file)}
                    aria-label={`${language === 'ar' ? 'عرض' : 'View'} ${doc.title}`}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t.viewDocument}
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1 gradient-primary text-white border-0"
                    onClick={() => handleDownloadDocument(doc.file, doc.title)}
                    aria-label={`${language === 'ar' ? 'تحميل' : 'Download'} ${doc.title}`}
                  >
                    <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t.downloadDocument}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bank Information — request via official channels (no sensitive data in client) */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-card to-primary/5">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl text-accent">{t.bankDetailsTitle}</CardTitle>
              <CardDescription>
                {isArabic
                  ? 'لأسباب أمنية، يتم تقديم تفاصيل الحساب البنكي عند الطلب الرسمي فقط.'
                  : 'For security reasons, banking details are shared only upon official request.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  asChild
                  variant="default"
                  className="gradient-primary text-white border-0"
                >
                  <a href="mailto:sales@arabianalwaha.com?subject=Bank%20Details%20Request">
                    <Mail className="w-4 h-4 mr-2" aria-hidden="true" />
                    {isArabic ? 'طلب عبر البريد الإلكتروني' : 'Request via Email'}
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <a href="tel:+966504838870">
                    <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                    <bdi dir="ltr" className="font-mono">+966 50 483 8870</bdi>
                  </a>
                </Button>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-6">
                {isArabic
                  ? 'سيقوم فريقنا بالرد بتفاصيل الحساب الرسمية بعد التحقق.'
                  : 'Our team will respond with verified account details after confirmation.'}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-primary/10 rounded-full mb-4">
            <FileText className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">
              {language === 'ar' ? 'جميع المستندات محدثة ومعتمدة رسمياً' : 
                'All documents are up-to-date and officially certified'}
            </span>
          </div>
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-yellow-100 rounded-full">
            <Award className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">
              {language === 'ar' ? 'الشهادات قيد التقدم' : 
                'Certifications are under progress'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalDocuments;
